import { OverallStatusFilter } from './api';
import { FormInfo } from './components/tasks/types';

/************************************************************
 *  TABLE FILTERS
 ************************************************************/

export enum RxFilterType {
  PRESCRIBER = 'prescriber',
  OFFICE = 'office',
  DRUG = 'drug',
  DRUG_CATEGORY = 'drug_category',
  STATUS = 'status',
  AWAITING_OFFICE = 'awaitingOffice',
  AWAITING_PATIENT = 'awaitingPatient',
  OVERALL_STATUS = 'overallStatus',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
}

export type RxStatus = 'in_progress' | 'pa_submitted' | 'approved' | 'denied' | 'appeal_submitted' | 'pa_not_required';

export type RxFilterValues = {
  [RxFilterType.PRESCRIBER]: string;
  [RxFilterType.OFFICE]: string;
  [RxFilterType.DRUG]: string;
  [RxFilterType.DRUG_CATEGORY]: string;
  [RxFilterType.STATUS]: string;
  [RxFilterType.AWAITING_OFFICE]: boolean;
  [RxFilterType.AWAITING_PATIENT]: boolean;
  [RxFilterType.OVERALL_STATUS]: OverallStatusFilter;
  [RxFilterType.START_DATE]: string;
  [RxFilterType.END_DATE]: string;
};

export type TasksFilterValues = {
  office: string[];
  prescriber: string[];
  type: string[];
  drug_name: string[];
  drug_category: string[];
  startDate: string;
  endDate: string;
};

export type TableName = 'prior_auth' | 'prior_auth_reviews' | 'tasks' | 'rx' | 'notifications';
// the table columns that we currently track in cookies
export type CookieColumnName = 'office_name' | 'drug_name' | 'drug_category';

/************************************************************
 *  SIGNUP FLOW
 ************************************************************/
// Valid job title values for org members. Keep in sync with python/partner_portal/blueprints/settings.py
export type Role =
  | 'administrative_assistant'
  | 'medical_assistant'
  | 'nurse'
  | 'nurse_practitioner'
  | 'office_manager'
  | 'physician'
  | 'physician_assistant'
  | 'practice_manager'
  | 'prior_authorization_specialist'
  | 'it_admin'
  | 'executive'
  | 'other';

export type Credential = 'md' | 'do' | 'pa' | 'np' | 'rn' | 'other' | 'none';

export type HereAs =
  | 'prescriber'
  | 'prescriber_delegate'
  | 'patient'
  | 'pharmaceutical_company'
  | 'insurance_plan'
  | 'pharmacy';

export type Affiliation =
  | 'healthcare_practice'
  | 'patient'
  | 'pharmaceutical_company'
  | 'insurance_company'
  | 'pharmacy';

export type HeardAboutFrom =
  | 'my_practice'
  | 'another_practice'
  | 'pharmaceutical_rep'
  | 'tandem'
  | 'event'
  | 'pharmacy'
  | 'patient'
  | 'search_engine'
  | 'social_media'
  | 'physical_mail'
  | 'other';

export type Specialty =
  | 'allergy_and_immunology'
  | 'cardiology'
  | 'dermatology'
  | 'endocrinology'
  | 'gastroenterology'
  | 'obstetrics_and_gynecology'
  | 'hepatology'
  | 'internal_medicine'
  | 'neurology'
  | 'oncology'
  | 'ophthalmology'
  | 'pediatrics'
  | 'primary_care'
  | 'psychiatry'
  | 'pulmonology'
  | 'rheumatology'
  | 'urology'
  | 'multi_specialty'
  | 'none'
  | 'other';

export type Ehr =
  | 'advancedmd'
  | 'athena'
  | 'cerner'
  | 'curemd'
  | 'drchrono'
  | 'eclinicalworks'
  | 'elation'
  | 'ema_modmed'
  | 'epic'
  | 'ezderm'
  | 'ggastro_modmed'
  | 'greenway'
  | 'iclinic_mdland'
  | 'next_gen'
  | 'nextech'
  | 'practice_fusion'
  | 'praxis'
  | 'veradigm'
  | 'other'
  | 'none';

export type OrganizationType = 'independent' | 'part_of_midmarket_group' | 'part_of_enterprise_group';

export type ProviderSignupSource = 'signup_flow' | 'onboarding_flow' | 'demo_flow' | 'other';

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

export interface SignupFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: Role;
  credentials: Credential;
  specify_other_credentials?: string;
  here_as: HereAs;
  heard_about_from: HeardAboutFrom;
  specify_other_heard_about_from: string;
}

export interface MoreInfoFormData {
  practice_name: string;
  specialty?: Specialty;
  specify_other_specialty?: string;
  size_numeric?: number;
  ehr?: Ehr;
  specify_other_ehr?: string;
  npi?: string;
  organization_type?: OrganizationType;
}

export interface AcceptTermsFormData {
  accepted_terms_at: string;
  tos_s3_key: string;
  tos_version_accepted: string;
  baa_s3_key: string;
  baa_version_accepted: string;
  aa_s3_key: string;
  aa_version_accepted: string;
}

// This is a combination of the above form data interface,
// but these fields are typed as optional because we want to
// allow for partial submissions
export interface AllFormData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  contact_phone?: string;
  role?: Role;
  affiliation?: Affiliation;
  credentials?: Credential;
  specify_other_credentials?: string;
  here_as?: HereAs;
  heard_about_from?: HeardAboutFrom;
  specify_other_heard_about_from?: string;
  practice_name?: string;
  specialty?: Specialty;
  specify_other_specialty?: string;
  size_numeric?: number;
  ehr?: Ehr;
  specify_other_ehr?: string;
  npi?: string;
  organization_type?: OrganizationType;
  accepted_terms_at?: string;
  tos_s3_key?: string;
  tos_version_accepted?: string;
  baa_s3_key?: string;
  baa_version_accepted?: string;
  aa_s3_key?: string;
  aa_version_accepted?: string;
  source?: ProviderSignupSource;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  practice_website?: string;
  google_ad_click_id?: string;
}

/************************************************************
 *  REFERRAL
 ************************************************************/
export interface ReferralFormData {
  // the one doing the referring
  referrer_name: string;
  referrer_email: string;
  // the one being referred
  referred_practice_name: string;
  referred_contact_name: string;
  referred_contact_role: Role | '';
  referred_contact_email: string;
}

/************************************************************
 * ENROLLMENTS
 ************************************************************/
export enum EnrollmentType {
  financial_assistance = 'PAP',
  bridge = 'Bridge',
  hub = 'HUB',
}

export type PendingEnrollment = {
  drug_name: string;
  enrollment_type: keyof typeof EnrollmentType;
  template_name: string;
  embedded_form_url: string;
  form: FormInfo;
  patient_first_name: string;
  patient_last_name: string;
  dob: string;
  status: 'not_started' | 'patient_consent_received' | 'matched' | 'canceled';
  prescription_id: string;
  requires_signature: boolean;
};

/************************************************************
 * SETTINGS
 ************************************************************/

// Keep in sync with SupportedEhrName in python/models/ehr_credentials.py
export const SUPPORTED_EHR_NAMES = [
  'ema_modmed',
  'athena',
  'drchrono',
  'elation',
  'eclinicalworks',
  'next_gen',
  'ggastro_modmed',
] as const;

export type SupportedEhrName = (typeof SUPPORTED_EHR_NAMES)[number];

/************************************************************
 * PRODUCT NOTIFICATIONS
 ************************************************************/

// Keep in sync with python/models/provider_portal.py
export type ProductNotificationType = 'fax_verification_success';

export interface ProductNotificationResponse {
  product_notification_to_show: ProductNotificationType | null;
  product_notification_id: string | null;
}

// Pharma pilot drug names - keep in sync with python/models/prescription.py PharmaPilotDrug
export enum PharmaPilotDrug {
  OPZELURA = 'opzelura',
  DUPIXENT = 'dupixent',
  TREMFYA = 'tremfya',
  ZORYVE = 'zoryve',
}
